import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Copy, Check, Loader2, KeyRound } from 'lucide-react';
import { TokenChain } from './TokenChain';
import './AgentLoginModal.css';

// Agent re-auth login modal (Login design-sync, Content-Post #1276). Manual flow
// first (always-available, prominent — not a hidden fallback): scrape the auth URL
// (POST /api/auth/login-url), show it big + Copy, the user authorizes in their own
// browser, pastes the callback back, we normalize → CODE#STATE and paste it into the
// agent's tmux (POST /api/sessions/{name}/paste, backend fires the 2nd Enter itself).
// The auto-flow (mode=both progress + verify) slots in once those endpoints land.

interface LoginUrlResponse {
  status: string;
  auth_url?: string;
  session?: string;
  owner_email?: string;
  profile?: string;
  already_logged_in?: boolean;
  message?: string;
  error?: string;
  // mode=both extras + the 502-safe-forward wrapper (Login #1276)
  progress_url?: string;
  poll_interval_ms?: number;
  http_status?: number;
  hint?: string;
  // Auto-flow skip reason — e.g. a human-interactive session that is manual-/login
  // only (agent Cloud). Then the top-level error is a misleading "URL scrape failed";
  // the real story is here and we fall through to the working manual URL flow.
  detail?: { status?: string; reason?: string; session?: string };
}

// Auto-flow progress-state → UI treatment (Login login-progress contract, #1276 +
// fail-fast states cloudflare_challenge/manual_required + queued, Login 2026-07-03).
function autoInfo(state: string, queuePos?: number): { kind: 'info' | 'warn' | 'err' | 'ok'; label: string } {
  switch (state) {
    case 'queued': return { kind: 'info', label: `Auto-Login wartet${queuePos ? ` (Platz ${queuePos})` : ' (Warteschlange)'} — Manual unten geht sofort ↓` };
    case 'running': return { kind: 'info', label: 'Auto-Login läuft…' };
    case 'success': return { kind: 'ok', label: 'Auto-Login erfolgreich' };
    case 'reauth_required': return { kind: 'warn', label: 'Anthropic verlangt Re-Auth — bitte unten manuell fortfahren ↓' };
    case 'cloudflare_challenge': return { kind: 'warn', label: 'Cloudflare-Challenge — Auto geht nicht, bitte unten manuell fortfahren ↓' };
    case 'manual_required': return { kind: 'warn', label: 'Auto-Login nicht möglich — bitte unten manuell fortfahren ↓' };
    case 'manual_pending': return { kind: 'warn', label: 'Auto-Login blockiert (Cloudflare/Timeout) — bitte unten manuell fortfahren ↓' };
    case 'failed': return { kind: 'err', label: 'Auto-Login fehlgeschlagen — bitte unten manuell fortfahren ↓' };
    default: return { kind: 'info', label: '' };
  }
}

/**
 * Normalize whatever the user pastes to the exact string claude-cli expects:
 * `CODE#STATE` (literal `#`), or just `CODE` when there's no state (Login,
 * auto_login.py:1805). Accepts: full callback URL, bare query, already-`CODE#STATE`,
 * or code-only.
 */
export function normalizeCallback(input: string): string {
  const s = input.trim();
  if (!s) return '';
  // Already `CODE` or `CODE#STATE` (no url/query syntax) → pass through.
  if (/^[A-Za-z0-9_-]+(#[A-Za-z0-9_-]+)?$/.test(s)) return s;
  // Pull code + optional state out of a full URL or bare query.
  const m = s.match(/code=([A-Za-z0-9_-]+)(?:[&#].*?state=([A-Za-z0-9_-]+))?/);
  if (m) return m[2] ? `${m[1]}#${m[2]}` : m[1];
  return s; // fall back to raw — backend may still accept it
}

/** Pull the OAuth `state` out of an auth URL or a normalized CODE#STATE. */
export function extractState(s: string): string | null {
  const url = s.match(/[?&#]state=([A-Za-z0-9_-]+)/);
  if (url) return url[1];
  const cs = s.match(/^[A-Za-z0-9_-]+#([A-Za-z0-9_-]+)$/); // CODE#STATE
  return cs ? cs[1] : null;
}

type Phase = 'fetching' | 'ready' | 'already' | 'pasting' | 'verifying' | 'verified' | 'verifyfail' | 'error' | 'codex';

// Codex host-auth status (Login GET /api/auth/token-status/{name}, Codex shape).
interface CodexStatus {
  // 'unresolved' (Login ab760c9): the federation resolver hiccuped — state UNKNOWN,
  // NOT a real "needs login". needs_login comes back null then. Treat neutrally.
  verdict?: 'healthy' | 'refreshable' | 'expired_no_recovery' | 'missing' | 'error' | 'unresolved';
  remaining_minutes?: number;
  auth_mode?: string;          // "chatgpt" | "apikey" | …
  needs_login?: boolean | null;
  recommended_action?: string; // none | restart | codex_login | login | retry | …
}

// Map a Codex verdict to a UI treatment (kind + human line). Kept out of the
// component so the mapping is testable and the JSX stays flat.
function codexVerdictView(s: CodexStatus | null): { kind: 'ok' | 'info' | 'warn' | 'err'; text: string } {
  if (!s || !s.verdict) return { kind: 'info', text: 'Codex-Login-Status wird geladen…' };
  const mins = s.remaining_minutes;
  const left = typeof mins === 'number'
    ? (mins >= 120 ? `noch ~${Math.round(mins / 60)} h` : `noch ${Math.max(0, Math.round(mins))} min`)
    : '';
  switch (s.verdict) {
    case 'healthy': return { kind: 'ok', text: `Codex-Login gültig${left ? ` — ${left}` : ''}.` };
    case 'refreshable': return { kind: 'info', text: `Codex-Login läuft (wird automatisch erneuert)${left ? ` — ${left}` : ''}.` };
    case 'expired_no_recovery': return { kind: 'warn', text: 'Codex-Login abgelaufen — ein Host-Login ist nötig.' };
    case 'missing': return { kind: 'warn', text: 'Kein Codex-Login vorhanden — ein Host-Login ist nötig.' };
    // Resolver hiccup — unknown, NOT "needs login". Neutral, no prompt.
    case 'unresolved': return { kind: 'info', text: 'Codex-Login-Status wird geladen… (Federation-Lookup)' };
    default: return { kind: 'err', text: 'Codex-Login-Status nicht ermittelbar.' };
  }
}

export function AgentLoginModal({ sessionName, mode = 'both', agent, onClose }: { sessionName: string; mode?: 'both' | 'manual'; agent?: string; onClose: () => void }) {
  // Codex agents don't use Claude's per-bot code#state OAuth — their auth is
  // host-file-based (~/.codex/auth.json, shared per node) and needs NO paste-back
  // (Login contract 2026-07-14). Branch away from the Claude flow so a Codex agent
  // never gets the wrong platform.claude.com code#state dialog. The concrete
  // status/reseed/api-key actions wire in once Login finalizes the Codex endpoints.
  const isCodex = (agent || '').toLowerCase() === 'codex';
  const [phase, setPhase] = useState<Phase>('fetching');
  const [data, setData] = useState<LoginUrlResponse | null>(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [callback, setCallback] = useState('');
  const [verify, setVerify] = useState<{ kind: 'ok' | 'err' | 'warn'; msg: string } | null>(null);
  const [progressUrl, setProgressUrl] = useState<string | null>(null);
  const [auto, setAuto] = useState<{ state: string; step?: string; detail?: string; queuePos?: number } | null>(null);
  // Set when Auto-Login was skipped for a human-interactive session and we fell
  // through to the manual URL flow — shown as a calm note above the auth URL.
  const [manualFallback, setManualFallback] = useState<string | null>(null);
  // Codex host-auth status (Login token-status Codex-shape, #1 ff2eb02).
  const [codexStatus, setCodexStatus] = useState<CodexStatus | null>(null);
  const pollMsRef = useRef(2000);
  // Last CODE#STATE we actually sent — OAuth codes are single-use, so a second
  // submit of the identical code is a guaranteed 401 (Login 2026-07-11). Reset
  // whenever a fresh auth URL is fetched.
  const lastSubmitRef = useRef<string | null>(null);

  // mode=both: start the AUTO flow in the background AND get the manual auth_url in the
  // SAME call — both paths visible, no switching (Alex's vision, #1276). ~13s sync.
  // mode=manual (#228): URL-scrape ONLY (/login-url) — no Chrome auto-flow, no lock
  // contention, no waiting on the queue; for "ich hab ein Problem, lass mich direkt".
  const startLogin = useCallback(async () => {
    // Codex: skip the entire Claude OAuth flow (login-url / code#state / verify).
    // Instead fetch the host-auth status (Login token-status Codex shape).
    if (isCodex) {
      setPhase('codex');
      setCodexStatus(null);
      const load = async (): Promise<CodexStatus | null> => {
        try {
          const r = await fetch(`/api/auth/token-status/${sessionName}`);
          const s = await r.json().catch(() => null);
          return s && typeof s === 'object' ? (s as CodexStatus) : null;
        } catch { return null; }
      };
      let s = await load();
      // 'unresolved' = federation-resolver hiccup (Login ab760c9), not a real state —
      // give it one neutral retry before showing it (backend also retries once).
      if (s?.verdict === 'unresolved') {
        await new Promise((r) => setTimeout(r, 2000));
        s = (await load()) ?? s;
      }
      if (s) setCodexStatus(s);
      return;
    }
    setPhase('fetching');
    setErr('');
    setAuto(null);
    setProgressUrl(null);
    setManualFallback(null);
    lastSubmitRef.current = null; // fresh flow → a previously-sent code may legitimately re-send
    try {
      const res = mode === 'manual'
        ? await fetch('/api/auth/login-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session: sessionName, force: true }),
          })
        : await fetch('/api/auth/login-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session: sessionName, mode: 'both', force: false }),
          });
      let d: LoginUrlResponse = await res.json();
      // Auto-flow was skipped because this is a human-interactive session (agent Cloud:
      // "manual /login only"). The top-level "URL scrape failed" is misleading — the
      // manual URL scrape works fine, so transparently fall through to it instead of
      // dead-ending on a red error + a retry that can never succeed.
      if (d.status === 'error' && mode !== 'manual' && d.detail?.status === 'skipped') {
        const man = await fetch('/api/auth/login-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session: sessionName, force: true }),
        });
        const md: LoginUrlResponse = await man.json();
        if (md.auth_url) {
          setManualFallback(d.detail?.reason || 'Auto-Login für diese Session nicht möglich — manueller Login:');
          d = md;
        }
      }
      setData(d);
      if (d.status === 'error') { setErr(d.hint || d.error || 'Login-Start fehlgeschlagen'); setPhase('error'); return; }
      if (d.already_logged_in) { setPhase('already'); return; }
      if (d.auth_url) {
        if (d.progress_url) { pollMsRef.current = d.poll_interval_ms || 2000; setProgressUrl(d.progress_url); }
        setPhase('ready');
      } else { setErr('Keine Auth-URL erhalten'); setPhase('error'); }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Netzwerkfehler');
      setPhase('error');
    }
  }, [sessionName, mode, isCodex]);

  useEffect(() => { void startLogin(); }, [startLogin]);

  // Poll the auto-flow progress. success → treat like a verified login; a non-running
  // terminal state (reauth/manual_pending/failed) stops the poll and the manual section
  // takes over (its nudge sharpens via the auto header).
  useEffect(() => {
    if (!progressUrl) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const res = await fetch(progressUrl);
        const p: { state?: string; step?: string; detail?: string; queue_position?: number } = await res.json();
        if (stopped) return;
        const st = String(p.state || 'unknown');
        setAuto({ state: st, step: p.step, detail: p.detail, queuePos: p.queue_position });
        if (st === 'success') {
          stopped = true;
          // Don't trust the auto-flow's self-reported success blindly. The progress
          // endpoint can report a STALE/early success (leftover terminal state from a
          // prior flow) on the very first poll — so the UI announced "Auto-Login
          // erfolgreich" before the user had even pasted her code (Alex's Kundin,
          // 2026-07-14). Confirm against the REAL token via verify-login (Login's
          // reliable source: a valid, non-expired token file) before declaring victory.
          setPhase('verifying');
          setVerify(null);
          try {
            const vr = await fetch('/api/auth/verify-login', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session: sessionName }),
            });
            const vd: { verified?: boolean } = await vr.json().catch(() => ({}));
            if (vd.verified) {
              setVerify({ kind: 'ok', msg: 'Auto-Login erfolgreich — der Agent ist eingeloggt.' });
              setPhase('verified');
            } else {
              // Early/false success — the token isn't actually valid yet. Return to the
              // manual flow so the user can finish by pasting her code below.
              setVerify({ kind: 'warn', msg: 'Auto-Login noch nicht bestätigt — bitte unten den Code aus der geöffneten URL einfügen.' });
              setPhase('ready');
            }
          } catch {
            setPhase('ready'); // verify unreachable → let the manual flow proceed
          }
          return;
        }
        // queued = still waiting for the shared Chrome lock — keep polling (Login 2026-07-03).
        if (st !== 'running' && st !== 'queued') { stopped = true; return; }
      } catch { if (stopped) return; /* transient → keep polling */ }
      if (!stopped) timer = setTimeout(tick, pollMsRef.current);
    };
    timer = setTimeout(tick, pollMsRef.current);
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [progressUrl]);

  const copyUrl = async () => {
    if (!data?.auth_url) return;
    try {
      await navigator.clipboard.writeText(data.auth_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  const submitCallback = async () => {
    const text = normalizeCallback(callback);
    if (!text) return;

    // Guard 1 — stale state (Login 2026-07-11): the pasted code#state must belong to
    // the CURRENT auth URL's flow. OAuth is PKCE-bound; a code from an OLDER URL
    // (e.g. a re-trigger regenerated the flow after the user opened the link) matches
    // a different code_verifier → Anthropic rejects the exchange with 401, which we
    // mislabeled as "token abgelehnt". Catch it here with a precise message.
    const pastedState = extractState(text);
    const expectedState = data?.auth_url ? extractState(data.auth_url) : null;
    if (expectedState && pastedState && pastedState !== expectedState) {
      setVerify({ kind: 'err', msg: 'Dieser Code gehört zu einem älteren Login (State passt nicht zur aktuellen URL). Bitte die URL oben NEU öffnen, dort autorisieren und den frischen Code einfügen.' });
      setPhase('verifyfail');
      return;
    }

    // Guard 2 — single-use: an OAuth code can be exchanged exactly once. Re-sending
    // the same code (e.g. a second click after a failed verify) is a guaranteed 401.
    if (text === lastSubmitRef.current) {
      setVerify({ kind: 'err', msg: 'Dieser Code wurde bereits abgeschickt — OAuth-Codes sind einmalig. Bitte die URL oben neu öffnen und einen frischen Code einfügen.' });
      setPhase('verifyfail');
      return;
    }
    lastSubmitRef.current = text;

    setProgressUrl(null); // user chose manual → stop the auto-flow poll
    setPhase('pasting');
    try {
      const res = await fetch(`/api/sessions/${sessionName}/paste`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, press_enter: true, confirm_after_enter: true }),
      });
      // Login's paste-side state-mismatch guard returns HTTP 200 with a plain dict
      // {error:"stale_state", hint:"…"} (NOT a 4xx) — so we MUST inspect the body
      // even on res.ok, else a guarded (un-pasted) stale code reads as success and
      // runVerify() then fails with the misleading "token abgelehnt" (Login 2026-07-11).
      const body = await res.json().catch(() => null);
      if (!res.ok || body?.error) {
        // stale_state (or any error slug) → stop here with the precise reason; the
        // code was NOT pasted, so verifying would be wrong.
        setVerify({ kind: 'err', msg: body?.hint || body?.error || `Paste fehlgeschlagen (HTTP ${res.status})` });
        setPhase('verifyfail');
        return;
      }
      void runVerify();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Paste fehlgeschlagen');
      setPhase('error');
    }
  };

  // Roundtrip-Check after the paste (Login /api/auth/verify-login #1276): pings the
  // agent, waits ≤20s. Maps the verdict to a clear ✓ / ✗ / ⚠ per Login's contract.
  const runVerify = async (attempt = 0) => {
    setPhase('verifying');
    if (attempt === 0) setVerify(null);
    try {
      const res = await fetch('/api/auth/verify-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session: sessionName }),
      });
      const d: { verified?: boolean; error?: string } = await res.json();
      if (d.verified) {
        setVerify({ kind: 'ok', msg: 'Login verifiziert — der Agent antwortet.' });
        setPhase('verified');
        return;
      }
      const e = String(d.error || '');
      const is401 = /401|re-?login|needs/i.test(e);
      // Timing false-negative (Issue #275): the token FILE write can lag the tmux
      // "logged in success" by a couple seconds, so a verify fired right after the
      // paste reads no-token → deep-ping → stale-401 match, though the agent IS
      // logged in. The pasted state matched the URL (not a stale code). So retry
      // the 401-verdict a few times before declaring failure — a genuinely rejected
      // token stays rejected across retries, a still-settling one flips to verified.
      if (is401 && attempt < 3) {
        setVerify({ kind: 'warn', msg: `Token wird noch aktiviert… (Prüfung ${attempt + 2}/4)` });
        await new Promise((r) => setTimeout(r, 3000));
        return runVerify(attempt + 1);
      }
      if (is401) setVerify({ kind: 'err', msg: 'Bot antwortet, aber Anthropic lehnt den Token noch ab. Falls im Terminal „logged in" steht, kurz warten und „Nochmal prüfen" — sonst den Login mit frischem Code wiederholen.' });
      else if (/timeout|no response/i.test(e)) setVerify({ kind: 'warn', msg: 'Bot antwortet gerade nicht — evtl. beschäftigt. Später nochmal prüfen.' });
      else if (/not found/i.test(e)) setVerify({ kind: 'err', msg: 'Bot in der Federation nicht gefunden.' });
      else setVerify({ kind: 'err', msg: e || 'Verifizierung fehlgeschlagen.' });
      setPhase('verifyfail');
    } catch {
      setVerify({ kind: 'warn', msg: 'Verify-Endpoint nicht erreichbar — der Login kann trotzdem geklappt haben.' });
      setPhase('verifyfail');
    }
  };

  return (
    <div className="lgm-backdrop" onClick={onClose}>
      <div className="lgm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lgm-head">
          <span className="lgm-title"><KeyRound size={15} /> Login — {sessionName}</span>
          <button type="button" className="lgm-x" onClick={onClose} aria-label="Schließen"><X size={16} /></button>
        </div>

        {/* Der Weg zum jetzigen Zustand — beantwortet „warum hat der Neustart
            nicht geholfen?", was ein reines Urteil offenlässt (Alex, #4528).
            Steht bewusst GANZ OBEN: wer den Dialog öffnet, weil etwas klemmt,
            liest zuerst, was passiert ist, und entscheidet dann. */}
        <div className="lgm-body lgm-chain-slot">
          <TokenChain sessionName={sessionName} />
        </div>

        {phase === 'fetching' && (
          <div className="lgm-body lgm-center">
            <Loader2 size={22} className="lgm-spin" />
            <span>Login-URL wird geholt… <span className="lgm-muted">(~18 s)</span></span>
          </div>
        )}

        {phase === 'codex' && (() => {
          const v = codexVerdictView(codexStatus);
          return (
            <div className="lgm-body">
              <div className={`lgm-auto lgm-auto-${v.kind}`}>
                <span className="lgm-auto-icon">{v.kind === 'ok' ? '✓' : v.kind === 'err' || v.kind === 'warn' ? '⚠' : 'ℹ'}</span>
                <span>{v.text}</span>
              </div>
              {codexStatus?.auth_mode && (
                <div className="lgm-ctx">
                  Konto: {codexStatus.auth_mode === 'chatgpt' ? 'ChatGPT (geteilter Host-Login)' : codexStatus.auth_mode === 'apikey' ? 'OpenAI API-Key' : codexStatus.auth_mode}
                </div>
              )}
              <p className="lgm-muted" style={{ lineHeight: 1.5, margin: '8px 0 0' }}>
                Codex authentifiziert host-basiert (geteilte <code>~/.codex/auth.json</code> pro Node) —
                es gibt keinen Code zum Einfügen. {codexStatus?.verdict === 'unresolved'
                  ? 'Der Status-Lookup zuckte gerade — der Status wird neu geprüft.'
                  : codexStatus?.recommended_action === 'restart'
                    ? 'Ein Neustart des Agenten übernimmt die aktuelle Host-Auth.'
                    : (codexStatus?.needs_login || codexStatus?.verdict === 'missing' || codexStatus?.verdict === 'expired_no_recovery')
                      ? 'Ein Host-Login (codex login am Node) ist nötig, um die Auth zu (re)aktivieren.'
                      : 'Solange der Host-Login gültig ist, ist nichts weiter zu tun.'}
              </p>
              <div style={{ marginTop: 14 }}>
                <button type="button" className="lgm-btn" onClick={onClose}>Schließen</button>
              </div>
            </div>
          );
        })()}

        {phase === 'already' && (
          <div className="lgm-body lgm-center">
            <Check size={22} className="lgm-ok" />
            <span>{data?.message || 'Agent ist bereits eingeloggt.'}</span>
            <button type="button" className="lgm-btn" onClick={onClose}>Schließen</button>
          </div>
        )}

        {phase === 'error' && (
          <div className="lgm-body lgm-center">
            <span className="lgm-err">⚠ {err}</span>
            <button type="button" className="lgm-btn" onClick={() => void startLogin()}>Erneut versuchen</button>
          </div>
        )}

        {(phase === 'ready' || phase === 'pasting' || phase === 'verifying' || phase === 'verified' || phase === 'verifyfail') && data?.auth_url && (
          <div className="lgm-body">
            {/* Auto-flow progress (mode=both) — runs in the background; on success it
                closes with ✓, otherwise it sharpens the manual nudge (#1276). */}
            {auto && phase !== 'verified' && autoInfo(auto.state, auto.queuePos).label && (
              <div className={`lgm-auto lgm-auto-${autoInfo(auto.state, auto.queuePos).kind}`}>
                {auto.state === 'running' || auto.state === 'queued'
                  ? <Loader2 size={14} className="lgm-spin" />
                  : <span className="lgm-auto-icon">{autoInfo(auto.state, auto.queuePos).kind === 'err' ? '✗' : '⚠'}</span>}
                <span>{autoInfo(auto.state, auto.queuePos).label}</span>
                {auto.state === 'running' || auto.state === 'queued' && auto.detail && <span className="lgm-muted">· {auto.detail}</span>}
              </div>
            )}

            {manualFallback && (
              <div className="lgm-auto lgm-auto-info">
                <span className="lgm-auto-icon">ℹ</span>
                <span>{manualFallback}</span>
              </div>
            )}

            {(data.owner_email || data.profile) && (
              <div className="lgm-ctx">{data.owner_email}{data.profile ? ` · ${data.profile}` : ''}</div>
            )}

            <div className="lgm-step"><span className="lgm-num">1</span> URL im eigenen Browser öffnen (saubere Anthropic-Session) + autorisieren:</div>
            <div className="lgm-url-row">
              <a className="lgm-url" href={data.auth_url} target="_blank" rel="noreferrer">{data.auth_url}</a>
              <button type="button" className="lgm-copy" onClick={copyUrl} title="URL kopieren">
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>

            <div className="lgm-step"><span className="lgm-num">2</span> Nach dem Autorisieren die Callback-URL (oder <code>code#state</code>) hier einfügen:</div>
            <textarea
              className="lgm-input"
              rows={2}
              placeholder="https://platform.claude.com/oauth/code/callback?code=…&state=…   —   oder   CODE#STATE"
              value={callback}
              onChange={(e) => setCallback(e.target.value)}
              disabled={phase === 'pasting' || phase === 'verifying' || phase === 'verified'}
            />

            {(phase === 'ready' || phase === 'pasting') && (
              <button type="button" className="lgm-btn lgm-primary" onClick={submitCallback} disabled={!callback.trim() || phase === 'pasting'}>
                {phase === 'pasting' ? <><Loader2 size={14} className="lgm-spin" /> Übermittle…</> : 'Absenden'}
              </button>
            )}

            {phase === 'verifying' && (
              <div className="lgm-done"><Loader2 size={16} className="lgm-spin" /> Login wird verifiziert… <span className="lgm-muted">(bis ~20 s)</span></div>
            )}

            {phase === 'verified' && (
              <div className="lgm-verified">
                <div className="lgm-done"><Check size={16} className="lgm-ok" /> {verify?.msg}</div>
                <button type="button" className="lgm-btn" onClick={onClose}>Fertig</button>
              </div>
            )}

            {phase === 'verifyfail' && (
              <div className={`lgm-vfail lgm-vfail-${verify?.kind}`}>
                <span>{verify?.kind === 'warn' ? '⚠' : '✗'} {verify?.msg}</span>
                <div className="lgm-vfail-actions">
                  <button type="button" className="lgm-btn" onClick={() => void runVerify()}>Nochmal prüfen</button>
                  <button type="button" className="lgm-btn lgm-primary" onClick={submitCallback} disabled={!callback.trim()}>Erneut absenden</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
